import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { getDashboardStats, getSubscriptionTrend, getDashboardDuesBuckets } from '@/api/dashboard';
import type { DashboardStats, TrendPoint, DuesBuckets } from '@/api/dashboard';
import client from '@/api/client';

/* ── Chart.js loaded from CDN via useEffect ─────────────────── */
declare global {
  interface Window {
    Chart: any;
  }
}

function loadChartJs(): Promise<void> {
  return new Promise((resolve) => {
    if (window.Chart) { resolve(); return; }
    const s = document.createElement('script');
    s.src = 'https://cdnjs.cloudflare.com/ajax/libs/Chart.js/4.4.1/chart.umd.js';
    s.onload = () => resolve();
    document.head.appendChild(s);
  });
}

/* ── Palette (must be hex — canvas can't read CSS vars) ─────── */
const TEAL        = '#0F6E56';
const TEAL_FILL   = 'rgba(15,110,86,0.10)';
const NAVY        = '#1B3A6B';
const GRID_COLOR  = 'rgba(0,0,0,0.06)';
const DONUT_PAID  = '#0F6E56';   // teal
const DONUT_UNPAID= '#E24B4A';   // red

/* ── Zone identity colours — fixed per zone number (1–8) ─────────── */
const ZONE_COLORS = ['#185FA5','#534AB7','#A32D2D','#0F6E56','#854F0B','#3B6D11','#993556','#993C1D'];

function zoneColor(zone: number): string {
  return ZONE_COLORS[(zone - 1) % ZONE_COLORS.length] ?? '#6B7280';
}

/* ── Trend line chart ────────────────────────────────────────── */
function TrendChart({ points }: { points: TrendPoint[] }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const chartRef  = useRef<any>(null);

  useEffect(() => {
    if (!points.length || !canvasRef.current) return;

    loadChartJs().then(() => {
      if (chartRef.current) { chartRef.current.destroy(); }

      const labels  = points.map((p) => p.label);
      const amounts = points.map((p) => p.totalCollected);

      // Show year tick only when year changes
      const tickLabels = labels.map((l, i) => {
        if (i === 0) return l;
        const yr = points[i].year;
        return points[i - 1].year !== yr ? l : l.split(' ')[0]; // "Jan" only if same year
      });

      chartRef.current = new window.Chart(canvasRef.current, {
        type: 'line',
        data: {
          labels: tickLabels,
          datasets: [{
            label: 'Amount collected (₹)',
            data: amounts,
            borderColor: TEAL,
            backgroundColor: TEAL_FILL,
            borderWidth: 2,
            pointRadius: points.length > 24 ? 2 : 4,
            pointHoverRadius: 6,
            pointBackgroundColor: TEAL,
            fill: true,
            tension: 0.35,
          }],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
            tooltip: {
              callbacks: {
                label: (ctx: any) => {
                  const p = points[ctx.dataIndex];
                  return [
                    `  Collected: ₹${ctx.parsed.y.toLocaleString('en-IN')}`,
                    `  Paid families: ${p.paidCount}`,
                    `  Unpaid: ${p.unpaidCount}`,
                  ];
                },
              },
            },
          },
          scales: {
            x: {
              grid: { color: GRID_COLOR },
              ticks: {
                font: { family: 'Georgia, serif', size: 11 },
                color: '#6B7280',
                maxRotation: 45,
                autoSkip: points.length > 18,
                maxTicksLimit: 18,
              },
            },
            y: {
              grid: { color: GRID_COLOR },
              beginAtZero: true,
              ticks: {
                font: { family: 'Georgia, serif', size: 11 },
                color: '#6B7280',
                callback: (v: number) => '₹' + v.toLocaleString('en-IN'),
              },
            },
          },
        },
      });
    });

    return () => { chartRef.current?.destroy(); };
  }, [points]);

  return (
    <div style={{ position: 'relative', width: '100%', height: '260px' }}>
      <canvas
        ref={canvasRef}
        role="img"
        aria-label="Line chart showing monthly subscription collection trend from Jan 2023 to present"
      >
        Monthly subscription trend from Jan 2023 to present.
      </canvas>
    </div>
  );
}

/* ── Paid vs Unpaid donut ────────────────────────────────────── */
function DonutChart({ paid, unpaid }: { paid: number; unpaid: number }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const chartRef  = useRef<any>(null);

  useEffect(() => {
    if (!canvasRef.current) return;
    loadChartJs().then(() => {
      if (chartRef.current) chartRef.current.destroy();
      chartRef.current = new window.Chart(canvasRef.current, {
        type: 'doughnut',
        data: {
          labels: ['Paid up', 'Have dues'],
          datasets: [{
            data: [paid, unpaid],
            backgroundColor: [DONUT_PAID, DONUT_UNPAID],
            borderWidth: 0,
            hoverOffset: 6,
          }],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          cutout: '68%',
          plugins: {
            legend: { display: false },
            tooltip: {
              callbacks: {
                label: (ctx: any) =>
                  `  ${ctx.label}: ${ctx.parsed} families`,
              },
            },
          },
        },
      });
    });
    return () => { chartRef.current?.destroy(); };
  }, [paid, unpaid]);

  const total = paid + unpaid || 1;
  const pct   = Math.round((paid / total) * 100);

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
      <div style={{ position: 'relative', width: '130px', height: '130px', flexShrink: 0 }}>
        <canvas
          ref={canvasRef}
          role="img"
          aria-label={`Donut chart: ${paid} families paid up, ${unpaid} have dues`}
        >
          {paid} paid, {unpaid} with dues.
        </canvas>
        {/* Centre label */}
        <div style={{
          position: 'absolute', inset: 0,
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          pointerEvents: 'none',
        }}>
          <span style={{ fontSize: '22px', fontWeight: 700, color: NAVY, fontFamily: 'Georgia, serif' }}>{pct}%</span>
          <span style={{ fontSize: '10px', color: '#6B7280', fontFamily: 'Georgia, serif' }}>paid up</span>
        </div>
      </div>

      {/* Legend */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {[
          { color: DONUT_PAID,   label: 'Paid up',   value: paid   },
          { color: DONUT_UNPAID, label: 'Have dues',  value: unpaid },
        ].map(({ color, label, value }) => (
          <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ width: '10px', height: '10px', borderRadius: '2px', background: color, flexShrink: 0 }} />
            <span style={{ fontSize: '12px', color: '#6B7280', fontFamily: 'Georgia, serif' }}>
              {label}
            </span>
            <span style={{ fontSize: '13px', fontWeight: 600, color: NAVY, fontFamily: 'Georgia, serif', marginLeft: '4px' }}>
              {value}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── Main page ───────────────────────────────────────────────── */
export default function DashboardPage() {
  const { user }    = useAuth();
  const navigate    = useNavigate();

  const [stats,   setStats]   = useState<DashboardStats | null>(null);
  const [trend,   setTrend]   = useState<TrendPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');

  useEffect(() => {
    let cancelled = false;
    Promise.all([getDashboardStats(), getSubscriptionTrend()])
      .then(([s, t]) => {
        if (cancelled) return;
        setStats(s);
        setTrend(t);
      })
      .catch(() => { if (!cancelled) setError('Failed to load dashboard data.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const topCards: { label: string; value: number | undefined; hint?: string; muted?: boolean; onClick?: () => void }[] = [
    { label: 'Total Families',      value: stats?.totalFamilies },
    { label: 'Total Members',       value: stats?.totalMembers },
    { label: 'Active Subscribers',  value: stats?.activeSubscribers },
    { label: 'Cemetery Records',    value: stats?.cemeteryRecords },
    { label: 'Certificates Issued', value: stats?.certificatesIssued, onClick: () => navigate('/certificates') },
    // Left the parish / moved anbiyam / duplicate record — excluded from
    // every family count, dues and progress figure on this page.
    { label: 'Inactive Families',   value: stats?.inactiveFamilies, muted: true,
      hint: 'Not counted in totals or dues', onClick: () => navigate('/families?status=inactive') },
    // Graves whose family isn't identified yet (held on the CHURCH placeholder).
    { label: 'Graves Not Linked',   value: stats?.unlinkedGraves, muted: true,
      hint: stats ? `${stats.unlinkedBurials ?? 0} burial records with no family` : undefined,
      onClick: () => navigate('/cemetery?linked=false') },
  ];

  const hasDues      = (stats?.familiesWithDues  ?? 0) > 0;
  const totalPending = stats?.totalOutstanding ?? 0;

  // Dues severity buckets — fetched independently (own endpoint, own
  // loading flag) so this heavier, multi-year scan never delays the rest
  // of the page; it just fills in this one card whenever it's ready.
  const [duesBuckets,        setDuesBuckets]        = useState<DuesBuckets | null>(null);
  const [duesBucketsLoading, setDuesBucketsLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    getDashboardDuesBuckets()
      .then((b) => { if (!cancelled) setDuesBuckets(b); })
      .catch(() => {/* non-critical — card just stays in its loading state */})
      .finally(() => { if (!cancelled) setDuesBucketsLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // Zone map summary (raw per-anbiyam rows, each has a zone number)
  const [zoneSummary, setZoneSummary] = useState<Array<{
    anbiyamId: number; anbiyamName: string; zone: number; totalFamilies: number; paidFamilies: number;
  }>>([]);
  useEffect(() => {
    const yr = new Date().getFullYear();
    client.get<{ data: { summary: typeof zoneSummary } }>(`/anbiyam/map-summary?year=${yr}`)
      .then((r) => setZoneSummary(r.data.data.summary))
      .catch(() => {/* non-critical */});
  }, []);

  // Trend summary numbers
  const totalCollectedAllTime = trend.reduce((s, p) => s + p.totalCollected, 0);
  const currentYear           = new Date().getFullYear();
  const thisYearPoints        = trend.filter((p) => p.year === currentYear);
  const thisYearCollected     = thisYearPoints.reduce((s, p) => s + p.totalCollected, 0);
  const lastPoint             = trend[trend.length - 1];

  // Paid vs unpaid families (this year)
  const paidFamilies   = stats ? stats.totalFamilies - stats.familiesWithDues : 0;
  const unpaidFamilies = stats?.familiesWithDues ?? 0;

  return (
    <div>
      <h1 className="font-georgia text-2xl font-bold text-navy mb-1">Dashboard</h1>
      <p className="font-georgia text-sm text-teal mb-6">
        Welcome back, <strong>{user?.username}</strong>
      </p>

      {error && <p className="font-georgia text-sm text-red-600 mb-4">{error}</p>}

      {/* Main stats */}
      <div className="grid grid-cols-1 sm:grid-cols-4 xl:grid-cols-7 gap-4 mb-6">
        {topCards.map(({ label, value, hint, muted, onClick }) => (
          <div
            key={label}
            className={`${muted ? 'bg-gray-50' : 'bg-white'} border border-grid-border p-5 ${onClick ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`}
            onClick={onClick}
            title={onClick ? `Open ${label}` : undefined}
          >
            <p className="font-georgia text-xs text-teal uppercase tracking-wide mb-2">{label}</p>
            <p className={`font-georgia text-3xl font-bold ${muted ? 'text-gray-500' : 'text-navy'}`}>
              {loading ? '…' : (value ?? '—')}
            </p>
            {hint && <p className="font-georgia text-xs text-gray-400 mt-1">{hint}</p>}
          </div>
        ))}
      </div>

      {/* Subscription dues cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
        <div
          className={`border p-5 cursor-pointer hover:shadow-md transition-shadow ${
            hasDues ? 'bg-amber-50 border-amber-300' : 'bg-green-50 border-green-300'
          }`}
          onClick={() => navigate('/subscriptions')}
          title="Click to open Subscriptions"
        >
          <p className="font-georgia text-xs uppercase tracking-wide mb-2 text-amber-700">
            Families with Dues (since Jan 2023)
          </p>
          <p className={`font-georgia text-3xl font-bold ${hasDues ? 'text-amber-700' : 'text-green-700'}`}>
            {loading ? '…' : (stats?.familiesWithDues ?? '—')}
          </p>
          {!loading && hasDues && (
            <p className="font-georgia text-xs text-amber-600 mt-1">
              ₹{totalPending.toLocaleString('en-IN')} outstanding — click to manage
            </p>
          )}
          {!loading && !hasDues && (
            <p className="font-georgia text-xs text-green-600 mt-1">All families up to date ✓</p>
          )}
        </div>

        <div
          className="bg-white border border-grid-border p-5 cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => navigate('/subscriptions')}
          title="Click to open Subscriptions"
        >
          <p className="font-georgia text-xs text-teal uppercase tracking-wide mb-2">
            Dues by Severity (since Jan 2023)
          </p>
          {duesBucketsLoading ? (
            <p className="font-georgia text-3xl font-bold text-navy">…</p>
          ) : !duesBuckets ? (
            <p className="font-georgia text-sm text-gray-400">Failed to load.</p>
          ) : (
            <div className="flex items-end gap-5">
              <div>
                <p className="font-georgia text-2xl font-bold text-amber-600">{duesBuckets.behind1to2}</p>
                <p className="font-georgia text-[11px] text-gray-500">1-2 months</p>
              </div>
              <div>
                <p className="font-georgia text-2xl font-bold text-orange-600">{duesBuckets.behind3to5}</p>
                <p className="font-georgia text-[11px] text-gray-500">3-5 months</p>
              </div>
              <div>
                <p className="font-georgia text-2xl font-bold text-red-600">{duesBuckets.behind6Plus}</p>
                <p className="font-georgia text-[11px] text-gray-500">6+ months</p>
              </div>
            </div>
          )}
          {!duesBucketsLoading && duesBuckets && (
            <p className="font-georgia text-xs text-teal mt-2">
              {duesBuckets.totalWithDues} families behind — click to manage
            </p>
          )}
        </div>
      </div>

      {/* Zone Cards — grouped by zone number (1–8) */}
      {zoneSummary.length > 0 && (() => {
        // Aggregate per-anbiyam rows into zone buckets
        const zoneMap: Record<number, { zone: number; totalFamilies: number; paidFamilies: number; anbiyams: string[] }> = {};
        zoneSummary.forEach((a) => {
          const z = a.zone || 0;
          if (!zoneMap[z]) zoneMap[z] = { zone: z, totalFamilies: 0, paidFamilies: 0, anbiyams: [] };
          zoneMap[z].totalFamilies += a.totalFamilies;
          zoneMap[z].paidFamilies  += a.paidFamilies;
          zoneMap[z].anbiyams.push(a.anbiyamName);
        });
        const zones = Object.values(zoneMap).sort((a, b) => a.zone - b.zone);

        return (
          <div className="mb-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
              <div>
                <p className="font-georgia text-sm font-bold text-navy">Parish Zones</p>
                <p className="font-georgia text-xs text-teal">Click a zone to explore families and subscription dues</p>
              </div>
              <button onClick={() => navigate('/anbiyam-map')} className="font-georgia text-xs text-teal hover:underline">
                View full map →
              </button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {zones.map((z) => {
                const color    = zoneColor(z.zone);
                const pct      = z.totalFamilies ? Math.round((z.paidFamilies / z.totalFamilies) * 100) : 0;
                const barColor = pct >= 80 ? '#059669' : pct >= 50 ? '#D97706' : '#DC2626';
                return (
                  <div
                    key={z.zone}
                    onClick={() => navigate(`/anbiyam-map?zone=${z.zone}`)}
                    className="overflow-hidden cursor-pointer hover:shadow-md transition-shadow"
                    style={{ border: '0.5px solid rgba(0,0,0,0.08)', borderRadius: '6px' }}
                    title={`Open Zone ${z.zone}`}
                  >
                    {/* Colored header */}
                    <div style={{ background: color, padding: '10px 12px' }}>
                      <p className="font-georgia" style={{ fontSize: '10px', color: 'rgba(255,255,255,0.7)', marginBottom: '2px' }}>
                        Zone {z.zone}
                      </p>
                      <p className="font-georgia" style={{ fontSize: '11px', fontWeight: 600, color: '#fff', lineHeight: 1.3, opacity: 0.9 }}>
                        {z.anbiyams.slice(0, 2).join(' · ')}{z.anbiyams.length > 2 ? ` +${z.anbiyams.length - 2}` : ''}
                      </p>
                    </div>
                    {/* Stats */}
                    <div style={{ background: '#fff', padding: '8px 12px' }}>
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="font-georgia" style={{ fontSize: '10px', color: '#6B7280' }}>
                          {z.totalFamilies} families
                        </span>
                        <span className="font-georgia" style={{ fontSize: '12px', fontWeight: 700, color }}>
                          {pct}%
                        </span>
                      </div>
                      <div style={{ height: '4px', background: '#F3F4F6', borderRadius: '2px', overflow: 'hidden' }}>
                        <div style={{ width: `${pct}%`, height: '100%', background: barColor, borderRadius: '2px' }} />
                      </div>
                      <p className="font-georgia" style={{ fontSize: '10px', color: '#9CA3AF', marginTop: '4px' }}>
                        {z.anbiyams.length} anbiyam{z.anbiyams.length !== 1 ? 's' : ''} · {z.paidFamilies} paid
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* ── Charts row ── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">

        {/* Line chart — takes 2/3 width */}
        <div className="lg:col-span-2 bg-white border border-grid-border p-5">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-4">
            <div>
              <p className="font-georgia text-sm font-bold text-navy">Collection Trend</p>
              <p className="font-georgia text-xs text-teal">Monthly amounts collected (Jan 2023 – now)</p>
            </div>
            {!loading && trend.length > 0 && (
              <div className="sm:text-right">
                <p className="font-georgia text-xs text-gray-500">This year</p>
                <p className="font-georgia text-base font-bold text-navy">
                  ₹{thisYearCollected.toLocaleString('en-IN')}
                </p>
              </div>
            )}
          </div>

          {loading && (
            <div className="flex items-center justify-center h-64 text-teal text-sm font-georgia">
              Loading chart…
            </div>
          )}
          {!loading && trend.length === 0 && (
            <div className="flex items-center justify-center h-64 text-gray-400 text-sm font-georgia">
              No subscription data found from Jan 2023 onwards.
            </div>
          )}
          {!loading && trend.length > 0 && (
            <>
              <TrendChart points={trend} />
              {/* Summary row */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4 pt-4 border-t border-gray-100">
                {[
                  { label: 'All-time collected', value: `₹${totalCollectedAllTime.toLocaleString('en-IN')}` },
                  { label: 'Months tracked',      value: String(trend.length) },
                  { label: `Last month (${lastPoint?.label ?? ''})`, value: `₹${(lastPoint?.totalCollected ?? 0).toLocaleString('en-IN')}` },
                ].map(({ label, value }) => (
                  <div key={label}>
                    <p className="font-georgia text-xs text-gray-400">{label}</p>
                    <p className="font-georgia text-sm font-bold text-navy">{value}</p>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Donut chart — takes 1/3 width */}
        <div className="bg-white border border-grid-border p-5">
          <p className="font-georgia text-sm font-bold text-navy mb-1">Families Status</p>
          <p className="font-georgia text-xs text-teal mb-5">Paid up vs outstanding dues</p>

          {loading ? (
            <div className="flex items-center justify-center h-40 text-teal text-sm font-georgia">
              Loading…
            </div>
          ) : (
            <DonutChart paid={paidFamilies} unpaid={unpaidFamilies} />
          )}

          {!loading && (
            <div className="mt-5 pt-4 border-t border-gray-100">
              <p className="font-georgia text-xs text-gray-400">Total families</p>
              <p className="font-georgia text-sm font-bold text-navy">{stats?.totalFamilies ?? '—'}</p>
            </div>
          )}
        </div>
      </div>

      {/* Footer note */}
      <div className="bg-white border border-grid-border p-6">
        <p className="font-georgia text-sm text-navy">
          Subscription dues are calculated from <strong>January 2023</strong> onwards.
          Active Subscribers shows families with at least one &lsquo;Paid&rsquo; month this year.
          Click the dues card above to manage subscriptions.
        </p>
      </div>
    </div>
  );
}
